import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, database } from '../../../firebase/firebase';
import { ref, update, push } from 'firebase/database';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningIcon from "@mui/icons-material/Warning";

const LogoutButton = ({ 
  variant = "contained", 
  color = "error",
  size = "medium",
  showConfirmation = true,
  onLogoutStart,
  onLogoutSuccess,
  onLogoutError 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const addActivityLog = async (userId, action, details) => {
    const logRef = ref(database, `users/${userId}/user_activity_logs`);
    await push(logRef, {
      timestamp: Date.now(),
      action,
      details
    });
  };

  const handleLogoutClick = () => {
    if (showConfirmation) {
      setShowConfirmDialog(true);
    } else {
      performLogout();
    }
  };

  const performLogout = async () => {
    setLoading(true);
    setShowConfirmDialog(false);
    
    if (onLogoutStart) {
      onLogoutStart();
    }

    try {
      const user = auth.currentUser;
      if (user) {
        // Add activity log for logout
        await addActivityLog(user.uid, 'Logout', 'User logged out successfully.');
        await update(ref(database, `users/${user.uid}`), {
          user_is_active: false,
          user_last_logout: Date.now()
        });
      }

      await signOut(auth);
      
      if (onLogoutSuccess) {
        onLogoutSuccess();
      }
      
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      
      if (onLogoutError) {
        onLogoutError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLogout = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <Button
        onClick={handleLogoutClick}
        variant={variant}
        color={color}
        size={size}
        startIcon={loading ? <CircularProgress size={16} /> : <LogoutIcon />}
        disabled={loading}
        sx={{ 
          textTransform: "none", 
          borderRadius: 2,
          px: 3,
          minWidth: 120
        }}
      >
        {loading ? 'Logging out...' : 'Logout'}
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelLogout}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="logout-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Logout Confirmation
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout? 
            Any unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCancelLogout} 
            variant="outlined"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={performLogout} 
            variant="contained" 
            color="error"
            startIcon={<LogoutIcon />}
            disabled={loading}
          >
            {loading ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

LogoutButton.propTypes = {
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  color: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showConfirmation: PropTypes.bool,
  onLogoutStart: PropTypes.func,
  onLogoutSuccess: PropTypes.func,
  onLogoutError: PropTypes.func
};

export default LogoutButton;